import type { Draft } from 'immer';
import Raven from 'raven-js';
import isEqual from 'hs-lodash/isEqual';
import type { ValueForKeyPath } from 'immutable-less/transmute';
import hasIn from 'transmute/hasIn';
import getIn from 'immutable-less/transmute/getIn';
import setIn from 'immutable-less/transmute/setIn';
import updateIn from 'immutable-less/transmute/updateIn';
import { produce } from 'immutable-less/internal/produce';
import { uniqueIdForObject } from 'immutable-less/utils/uniqueIdForObject';
import { ImmutableJSRecordMethodNames } from 'immutable-less/constants/ImmutableJSRecordMethodNames';

export type Predicate<RecordType, Iterable, ReturnType = boolean> = <
  Key extends keyof RecordType,
  Value extends RecordType[Key],
>(
  value: Value,
  key: Key,
  iterable: Iterable
) => ReturnType;

type Entry<ObjectType> = [keyof ObjectType, ObjectType[keyof ObjectType]];

export type Reducer<Reduction, RecordType, Iterable> = <
  Key extends keyof RecordType,
  Value extends RecordType[Key],
>(
  reduction: Reduction,
  value: Value,
  key: Key,
  iterable: Iterable
) => Reduction;

function bindOptionalContext<FunctionType extends Function, Context>(
  predicate: FunctionType,
  context: Context | undefined
): FunctionType {
  if (!context) {
    return predicate;
  } else {
    return predicate.bind(context) as FunctionType;
  }
}

export interface BaseImmutableObjectAbstractMethods<JSONType, RecordType> {
  clear(): this;
  toJSON<
    DerivedClass extends ImmutableObject<JSONType, RecordType>,
    DerivedJSONType = GetJSONType<DerivedClass>,
  >(
    this: DerivedClass
  ): DerivedJSONType;
}

export class BaseImmutableObject<JSONType, RecordType> {
  equals(that: this): boolean {
    return isEqual(this, that);
  }

  every<
    DerivedRecordType extends RecordType,
    Self extends BaseImmutableObject<JSONType, RecordType>,
    Context,
  >(
    this: Self,
    predicate: Predicate<DerivedRecordType, Self>,
    context?: Context
  ): boolean {
    const entries = Object.entries(this) as Entry<DerivedRecordType>[];
    const boundPredicate = bindOptionalContext(predicate, context);
    return entries.every(([key, value]) => boundPredicate(value, key, this));
  }

  // flatten() has no effect on Records
  flatten(__depth: number | boolean) {}

  forEach<
    DerivedRecordType extends RecordType,
    Self extends BaseImmutableObject<JSONType, RecordType>,
    Context,
  >(
    this: Self,
    predicate: Predicate<DerivedRecordType, Self, unknown>,
    context?: Context
  ): number {
    const entries = Object.entries(this) as Entry<DerivedRecordType>[];
    const boundPredicate = bindOptionalContext(predicate, context);
    let numIterations = 0;
    entries.every(([key, value]) => {
      numIterations += 1;
      return boundPredicate(value, key, this) !== false;
    });
    return numIterations;
  }

  get<Key extends keyof RecordType>(
    this: ImmutableObject<JSONType, RecordType>,
    key: Key,
    notSetValue?: RecordType[Key]
  ): RecordType[Key] {
    if (notSetValue !== undefined && !this.has(key)) {
      return notSetValue;
    } else {
      return this[key];
    }
  }

  getIn<
    KeyPath extends readonly any[],
    Self extends ImmutableObject<JSONType, RecordType>,
  >(
    this: Self,
    keyPath: KeyPath,
    notSetValue?: ValueForKeyPath<KeyPath, Self>
  ): ValueForKeyPath<KeyPath, Self> {
    if (notSetValue !== undefined && !this.hasIn(keyPath as Draft<KeyPath>)) {
      return notSetValue;
    } else {
      return getIn(keyPath, this);
    }
  }

  has<Key extends keyof RecordType>(key: Key): boolean {
    return Object.hasOwnProperty.call(this, key);
  }

  hasIn(keyPath: (string | number)[]): boolean {
    return hasIn(keyPath, this);
  }

  hashCode(): number {
    return uniqueIdForObject(this);
  }

  map<
    DerivedRecordType extends RecordType,
    Self extends BaseImmutableObject<JSONType, DerivedRecordType>,
    Context,
  >(
    this: Self,
    mapper: Predicate<DerivedRecordType, Self, unknown>,
    context?: Context
  ) {
    const entries = Object.entries(this) as Entry<DerivedRecordType>[];
    const boundMapper = bindOptionalContext(mapper, context);
    entries.forEach(([key, value]) => boundMapper(value, key, this));
  }

  reduce<
    DerivedRecordType extends RecordType,
    Self extends BaseImmutableObject<JSONType, DerivedRecordType>,
    Reduction extends RecordType[keyof RecordType],
  >(
    this: Self,
    reducer: Reducer<Reduction, DerivedRecordType, Self>
  ): Reduction;

  reduce<
    DerivedRecordType extends RecordType,
    Self extends BaseImmutableObject<JSONType, DerivedRecordType>,
    Reduction,
    Context,
  >(
    this: Self,
    reducer: Reducer<Reduction, DerivedRecordType, Self>,
    initialReduction: Reduction,
    context?: Context
  ): Reduction;

  reduce<
    DerivedRecordType extends RecordType,
    Self extends BaseImmutableObject<JSONType, DerivedRecordType>,
    Context,
  >(
    this: Self,
    reducer: Reducer<unknown, DerivedRecordType, Self>,
    initialReduction?: unknown,
    context?: Context
  ): unknown {
    const entries = Object.entries(this) as Entry<DerivedRecordType>[];
    const boundReducer = bindOptionalContext(reducer, context);
    if (arguments.length < 2) {
      return entries.reduce(([, reduction], [key, value]) => [
        key,
        boundReducer(
          reduction,
          value,
          key,
          this
        ) as DerivedRecordType[keyof DerivedRecordType],
      ])[1];
    } else {
      return entries.reduce<unknown>(
        (reduction, [key, value]) => boundReducer(reduction, value, key, this),
        initialReduction!
      );
    }
  }

  set<
    Self extends ImmutableObject<JSONType, RecordType>,
    Key extends keyof RecordType,
  >(this: Self, key: Key, value: RecordType[Key]): Self {
    if (this.has(key)) {
      return produce(this, (draft: RecordType) => {
        draft[key] = value;
      });
    } else {
      return this;
    }
  }

  setIn<
    Self extends ImmutableObject<JSONType, RecordType>,
    KeyPath extends readonly any[],
  >(this: Self, keyPath: KeyPath, value: ValueForKeyPath<KeyPath, Self>): Self {
    return setIn(keyPath, value, this);
  }

  some<
    DerivedRecordType extends RecordType,
    Self extends BaseImmutableObject<JSONType, DerivedRecordType>,
    Context,
  >(
    this: Self,
    predicate: Predicate<DerivedRecordType, Self>,
    context?: Context
  ): boolean {
    const entries = Object.entries(this) as Entry<DerivedRecordType>[];
    const boundPredicate = bindOptionalContext(predicate, context);
    return entries.some(([key, value]) => boundPredicate(value, key, this));
  }

  // sortBy() has no effect on Records
  sortBy<DerivedRecordType extends RecordType>(
    __comparatorValueMapper: Predicate<DerivedRecordType, this, number>,
    __comparator?: (a: number, b: number) => number
  ) {}

  update<Self extends ImmutableObject<JSONType, RecordType>>(
    this: Self,
    updater: (value: Draft<Self>) => Draft<Self>
  ): Self;

  update<Key extends keyof RecordType>(
    key: Key,
    updater: (value: RecordType[Key]) => RecordType[Key]
  ): this;

  update<Key extends keyof RecordType>(
    key: Key,
    notSetValue: RecordType[Key],
    updater: (value: RecordType[Key]) => RecordType[Key]
  ): this;

  update<
    Self extends ImmutableObject<JSONType, RecordType>,
    Key extends keyof RecordType,
  >(
    this: Self,
    ...args:
      | [(value: Draft<this>) => Draft<this>]
      | [Key, (value: RecordType[Key]) => RecordType[Key]]
      | [Key, RecordType[Key], (value: RecordType[Key]) => RecordType[Key]]
  ): Self {
    if (args.length === 1) {
      const [updater] = args;
      return produce(this, updater);
    } else {
      let key: Key;
      let updater: (value: RecordType[Key]) => RecordType[Key];
      let currentValue: RecordType[Key];

      if (args.length === 2) {
        [key, updater] = args;
        currentValue = this[key];
      } else {
        [key, currentValue, updater] = args;
        if (this.has(key)) {
          currentValue = this[key];
        }
      }

      return this.set(key, updater(currentValue));
    }
  }

  updateIn<
    Self extends ImmutableObject<JSONType, RecordType>,
    KeyPath extends readonly any[],
    Value extends ValueForKeyPath<KeyPath, Self>,
  >(this: Self, keyPath: KeyPath, updater: (value: Value) => Value): Self;

  updateIn<
    Self extends ImmutableObject<JSONType, RecordType>,
    KeyPath extends readonly any[],
    Value extends ValueForKeyPath<KeyPath, Self>,
  >(
    this: Self,
    keyPath: KeyPath,
    notSetValue: Value,
    updater: (value: Value) => Value
  ): Self;

  updateIn<
    Self extends ImmutableObject<JSONType, RecordType>,
    KeyPath extends readonly any[],
    Value extends ValueForKeyPath<KeyPath, Self>,
  >(
    this: Self,
    ...args:
      | [KeyPath, (value: Value) => Value]
      | [KeyPath, Value, (value: Value) => Value]
  ): Self {
    if (args.length === 2) {
      const [keyPath, updater] = args;
      return updateIn(keyPath, updater, this);
    } else {
      const [keyPath, notSetValue, updater] = args;
      if (!this.hasIn(keyPath as Draft<KeyPath>)) {
        return updateIn(keyPath, () => updater(notSetValue), this);
      } else {
        return updateIn(keyPath, updater, this);
      }
    }
  }

  toJS(this: ImmutableObject<JSONType, RecordType>): JSONType {
    return this.toJSON();
  }

  withMutations<Self extends ImmutableObject<JSONType, RecordType>>(
    this: Self,
    mutator: (mutable: Draft<Self>) => void
  ): Self {
    return produce(this, mutator);
  }
}

// strips the `abstract` modifiers from the methods while preserving the `this` type
type ImmutableObjectImplementation<JSONType, RecordType> = BaseImmutableObject<
  JSONType,
  RecordType
> &
  BaseImmutableObjectAbstractMethods<JSONType, RecordType>;

export type ImmutableObject<JSONType, RecordType = JSONType> = RecordType &
  ImmutableObjectImplementation<JSONType, RecordType>;

export type GetJSONType<Type> = Type extends ImmutableObjectImplementation<
  infer T,
  object
>
  ? T
  : never;

export type GetRecordType<Type> = Type extends ImmutableObjectImplementation<
  object,
  infer T
>
  ? T
  : never;

ImmutableJSRecordMethodNames.forEach((methodName) => {
  if (
    !Object.prototype.hasOwnProperty.call(
      BaseImmutableObject.prototype,
      methodName
    )
  ) {
    // @ts-ignore normally these would be tacked on to BaseImmutableObject's
    // type but this fallback is specifcally for code which is not typed
    BaseImmutableObject.prototype[methodName] = function () {
      const error = new Error(
        `Attempted to call unimplemented method '${methodName}()' on an ImmutableObject`
      );

      Raven.captureException(error);
    };
  }
});
